import { type AskPunttVersionComparison } from "@mg/schemas/src/christo/versionComparison";
import { Button, Text } from "@radix-ui/themes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAskPunttReview } from "./useAskPunttReview";

import { useAskPunttArtifactContext } from "../../components/ask-puntt-review";
import { reviewCommentGroup } from "../../utils/visibleReviewComments";

type AskV2VersionCheckCardProps = {
  /** The newer version's review, the one the version check answered with. */
  reviewId: string;
  comparison: AskPunttVersionComparison;
};

/**
 * The chat's card for a version check: which versions were compared, how many
 * areas changed, and the ways into the comparison and the newer review.
 */
export function AskV2VersionCheckCard({
  reviewId,
  comparison,
}: AskV2VersionCheckCardProps) {
  const { t } = useTranslation("ui");
  const artifacts = useAskPunttArtifactContext();
  const review = useAskPunttReview(reviewId).data?.review;
  const versionNumber = review?.versionNumber;
  const newerMark = versionNumber != null ? `V${versionNumber}` : null;
  // why versionNumber - 1: a review with a version number is one above the
  // review it was linked to (the schema's lineage contract).
  const olderMark =
    // Stryker disable next-line ConditionalExpression: forcing `versionNumber != null` true is equivalent (a missing number fails `> 1` anyway); forcing the whole check true prints "V0 → V1", which the first-version title test kills
    versionNumber != null && versionNumber > 1 ? `V${versionNumber - 1}` : null;
  const changedAreas = comparison.regions.length;
  // why: warhol drops windows to fit the payload and flags it, so a partial
  // comparison's count is a floor, not the whole difference.
  const changedAreasIsFloor =
    comparison.regionsTruncated || comparison.status === "partial";
  const { fixed, stillOpen } = useMemo(
    () => ({
      fixed: (review?.resolved ?? []).filter((entry) => !entry.reopenedAt)
        .length,
      stillOpen: (review?.comments ?? []).filter(
        (comment) =>
          !comment.isDeleted && reviewCommentGroup(comment) === "unresolved",
      ).length,
    }),
    [review?.comments, review?.resolved],
  );
  // why: the sheet is a signed link that expires on old chats; a dead one
  // drops out rather than drawing a broken image.
  const [sheetFailed, setSheetFailed] = useState(false);
  const onCompare = artifacts?.onCompareReview;
  const onOpen = artifacts?.onActivateReview;

  const tiles = [
    {
      key: "changed",
      count: changedAreasIsFloor ? `${changedAreas}+` : changedAreas,
      label: t("askV2.versionCheck.tileChanged", { count: changedAreas }),
      onClick: onCompare ? () => onCompare(reviewId) : undefined,
    },
    {
      key: "fixed",
      count: fixed,
      label: t("askV2.versionCheck.tileFixed", { count: fixed }),
      onClick: onOpen ? () => onOpen(reviewId) : undefined,
    },
    {
      key: "open",
      count: stillOpen,
      label: t("askV2.versionCheck.tileOpen", { count: stillOpen }),
      onClick: onOpen ? () => onOpen(reviewId) : undefined,
    },
  ];

  return (
    <section
      aria-label={t("askV2.versionCheck.label")}
      className="mt-3 flex flex-col gap-3 rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-4"
      data-testid={`ask-v2-chat-version-check-${reviewId}`}
    >
      <div className="flex gap-4">
        {comparison.sheet && !sheetFailed ? (
          <img
            src={comparison.sheet.source}
            alt=""
            role="presentation"
            className="h-24 w-32 shrink-0 rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 object-cover"
            data-testid={`ask-v2-chat-version-check-sheet-${reviewId}`}
            onError={() => setSheetFailed(true)}
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Text
            as="p"
            size="1"
            weight="bold"
            className="uppercase tracking-wide text-puntt-neutral-gray-11"
          >
            {t("askV2.versionCheck.eyebrow")}
          </Text>
          <Text
            as="p"
            size="3"
            weight="bold"
            className="truncate"
            data-testid={`ask-v2-chat-version-check-title-${reviewId}`}
          >
            {olderMark && newerMark
              ? `${olderMark} → ${newerMark}${review?.title ? ` · ${review.title}` : ""}`
              : (review?.title ?? t("askV2.versionCheck.eyebrow"))}
          </Text>
          <Text as="p" size="2" color="gray">
            {t(
              changedAreasIsFloor
                ? "askV2.versionCheck.changedAreasAtLeast"
                : "askV2.versionCheck.changedAreas",
              { count: changedAreas },
            )}
          </Text>
          {
            // Stryker disable next-line ConditionalExpression: forcing true adds an empty row with no control or text, which nothing a reader or screen reader sees; forcing false drops both buttons, which the button tests kill
            onCompare || onOpen ? (
              <div className="mt-1 flex flex-wrap gap-2">
                {onCompare ? (
                  <Button
                    size="2"
                    data-testid={`ask-v2-chat-version-check-open-${reviewId}`}
                    onClick={() => onCompare(reviewId)}
                  >
                    {t("askV2.versionCheck.openComparison")}
                  </Button>
                ) : null}
                {onOpen ? (
                  <Button
                    size="2"
                    variant="outline"
                    color="gray"
                    data-testid={`ask-v2-chat-version-check-review-${reviewId}`}
                    onClick={() => onOpen(reviewId)}
                  >
                    {newerMark
                      ? t("askV2.versionCheck.seeFullVersionReview", {
                          mark: newerMark,
                        })
                      : t("askV2.versionCheck.seeFullReview")}
                  </Button>
                ) : null}
              </div>
            ) : null
          }
        </div>
      </div>
      <div
        role="group"
        aria-label={t("askV2.versionCheck.impactLabel")}
        className="grid grid-cols-3 gap-2"
        data-testid={`ask-v2-chat-version-check-impact-${reviewId}`}
      >
        {tiles.map((tile) =>
          tile.onClick ? (
            <button
              key={tile.key}
              type="button"
              className="flex items-baseline gap-2 rounded-lg border border-puntt-neutral-gray-5 bg-base-white px-3 py-2 text-left hover:border-puntt-accent-7"
              data-testid={`ask-v2-chat-version-check-tile-${tile.key}-${reviewId}`}
              onClick={tile.onClick}
            >
              <span className="text-[18px] font-bold text-puntt-accent-9">
                {tile.count}
              </span>
              <span className="text-[12px] text-puntt-neutral-gray-11">
                {tile.label}
              </span>
            </button>
          ) : (
            <div
              key={tile.key}
              className="flex items-baseline gap-2 rounded-lg border border-puntt-neutral-gray-5 px-3 py-2"
              data-testid={`ask-v2-chat-version-check-tile-${tile.key}-${reviewId}`}
            >
              <span className="text-[18px] font-bold text-puntt-accent-9">
                {tile.count}
              </span>
              <span className="text-[12px] text-puntt-neutral-gray-11">
                {tile.label}
              </span>
            </div>
          ),
        )}
      </div>
    </section>
  );
}
